/**
 * Settings → Plugins → one package at a pinned version: what it does (its
 * jobs, apps, skills and deliverables), the projects it is already set up
 * in, and Install, which reads "Installing…" until Denker has the request,
 * then opens the chat where Denker sets it up. Update, Remove
 * and Export act on the package. A pinned version the catalog no longer
 * bundles still opens, with only Remove.
 */
import { useState } from "react";
import { Button, ConfirmDialog, PocketStatement } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsPluginsMessages } from "@/i18n/messages/settings-plugins";
import { openConversationInInputBar } from "@/lib/answer-in-input-bar";
import { PluginDetailSections } from "./plugin-detail-sections";
import {
  askDenkerInNewChat,
  usePluginProjects,
  useSetUpProjectNames,
} from "./plugin-projects-state";
import {
  usePluginActions,
  usePluginDetail,
  usePluginInstallations,
} from "./plugins-state";
import { SettingsLoadStatus } from "./settings-load-status";
import { useSettingsNav } from "./settings-navigation";

function RemoveAction({
  name,
  busy,
  removing,
  onRemove,
}: {
  name: string;
  busy: boolean;
  removing: boolean;
  onRemove: () => void;
}) {
  const t = useT(settingsPluginsMessages);
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        disabled={busy}
        data-testid="plugin-remove"
        onClick={() => setConfirm(true)}
      >
        {removing ? t("removing") : t("remove")}
      </Button>
      {confirm && (
        <ConfirmDialog
          title={t("removeTitle", { name })}
          description={t("removeKeeps")}
          confirmLabel={t("remove")}
          variant="danger"
          data-testid="plugin-remove-confirm"
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            onRemove();
          }}
        />
      )}
    </>
  );
}

export function PluginDetailPage({
  slug,
  version,
}: {
  slug: string;
  version: string;
}) {
  const t = useT(settingsPluginsMessages);
  const nav = useSettingsNav();
  const detail = usePluginDetail(slug, version);
  const installations = usePluginInstallations();
  const [removed, setRemoved] = useState(false);
  const [installFailed, setInstallFailed] = useState(false);
  const [installing, setInstalling] = useState(false);
  const plugin = detail.data;
  const canSetUp = Boolean(plugin?.project_setup);
  const instances = usePluginProjects(slug, canSetUp);
  const setUpIn = useSetUpProjectNames(instances.data).join(", ");
  const installation = installations.data.find((item) => item.slug === slug);
  const name = plugin?.name ?? installation?.name ?? slug;
  const actions = usePluginActions({
    slug,
    version,
    name,
    onChanged: () => {
      detail.retry();
      installations.retry();
    },
  });
  const busy = actions.busy !== null;
  const remove = (
    <RemoveAction
      name={name}
      busy={busy}
      removing={actions.busy === "remove"}
      onRemove={() => void actions.remove().then(setRemoved)}
    />
  );
  const notices = (
    <>
      {installations.error && (
        <SettingsLoadStatus
          state={installations}
          noun={t("installationStatusNoun")}
          retryTestId="plugin-installations-retry"
        />
      )}
      {instances.error && (
        <SettingsLoadStatus
          state={instances}
          noun={t("projectsUsingNoun")}
          retryTestId="plugin-instances-retry"
        />
      )}
      {(actions.error || installFailed) && (
        <p role="alert" className="px-2 text-appkit-small text-danger">
          {actions.error ?? t("errorInstall", { name })}
        </p>
      )}
      {removed && (
        <p
          role="status"
          className="px-2 text-appkit-small text-muted"
          data-testid="plugin-removed"
        >
          {t("removed", { name })}
        </p>
      )}
    </>
  );

  if (detail.unavailable)
    return (
      <div data-testid="settings-page-plugin">
        <PocketStatement
          size="panel"
          title={name}
          line={t("versionUnavailable", {
            version: installation?.version ?? version,
          })}
          action={installation ? remove : undefined}
          testId="plugin-unavailable"
        />
        {notices}
      </div>
    );
  if (!plugin)
    return (
      <div data-testid="settings-page-plugin">
        <SettingsLoadStatus
          state={detail}
          noun={t("thisPluginNoun")}
          retryTestId="plugin-detail-retry"
        />
      </div>
    );
  const update =
    plugin.installed && installation?.upgrade_available
      ? installation.latest_version
      : null;
  const install = async () => {
    setInstalling(true);
    setInstallFailed(false);
    const accepted = await askDenkerInNewChat(
      setUpIn
        ? t("setUpAgainRequest", { name: plugin.name, projects: setUpIn })
        : t("setUpRequest", { name: plugin.name }),
    ).catch(() => false);
    setInstalling(false);
    setInstallFailed(!accepted);
    if (!accepted) return;
    nav.close();
    void openConversationInInputBar(null);
  };
  return (
    <div data-testid="settings-page-plugin">
      <PocketStatement
        size="panel"
        title={plugin.name}
        line={
          <>
            <p>
              {plugin.installed && plugin.installed_version !== plugin.version
                ? t("versionOtherInstalled", {
                    version: plugin.version,
                    installed: plugin.installed_version ?? "",
                  })
                : t(plugin.installed ? "versionInstalled" : "version", {
                    version: plugin.version,
                  })}
            </p>
            {setUpIn && (
              <p data-testid="plugin-set-up-in">
                {t("setUpIn", { projects: setUpIn })}
              </p>
            )}
          </>
        }
        action={
          <>
            {canSetUp && (
              <Button
                size="sm"
                disabled={busy || installing}
                data-testid="plugin-install"
                onClick={() => void install()}
              >
                {installing
                  ? t("installing")
                  : setUpIn
                    ? t("setUpAgain")
                    : t("install")}
              </Button>
            )}
            {update && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                data-testid="plugin-update"
                onClick={() =>
                  void actions.upgrade(update).then((ok) => {
                    if (ok)
                      nav.open({
                        page: "plugins-plugin",
                        args: { slug, version: update, title: name },
                      });
                  })
                }
              >
                {actions.busy === "update"
                  ? t("updating")
                  : t("updateTo", { version: update })}
              </Button>
            )}
            {plugin.installed && remove}
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              data-testid="plugin-export"
              onClick={() => void actions.exportRecipe()}
            >
              {t("export")}
            </Button>
          </>
        }
      />
      {plugin.description && (
        <p className="px-2 pb-1.5 text-appkit-body text-secondary">
          {plugin.description}
        </p>
      )}
      {detail.error && (
        <SettingsLoadStatus
          state={detail}
          noun={t("thisPluginNoun")}
          retryTestId="plugin-detail-retry"
        />
      )}
      {notices}
      <PluginDetailSections detail={plugin} />
    </div>
  );
}
